import type { CollectionData, CollectionGroup, CollectionTreeNode, ProjectDoc } from '@tomo/core';
import { Store, useStore } from '../../lib/store.ts';

/**
 * The sidebar's groups — folders holding collections. A group is only a name and a place in the
 * list: the collections inside it are ordinary collections that happen to point at it, so a group
 * can be opened out again without anything moving. Groups never nest.
 *
 * This module holds the parts that aren't markup: which groups are folded shut (a per-device
 * preference, so it stays out of the document), and the rules that turn the list into rows and a
 * drop into a move.
 */

/* ---------------------------------------------------------------------------------------------- *
 * Row keys — one namespace for the drag and drop ids of every kind of row
 * ---------------------------------------------------------------------------------------------- */

const GROUP_ROW = 'g:';
const EMPTY_ROW = 'e:';

export const groupRowKey = (groupId: string) => `${GROUP_ROW}${groupId}`;
export const isGroupRow = (key: string) => key.startsWith(GROUP_ROW);
export const groupIdFromRow = (key: string) => key.slice(GROUP_ROW.length);

/* ---------------------------------------------------------------------------------------------- *
 * Folded groups, and the row waiting to be named
 * ---------------------------------------------------------------------------------------------- */

const STORAGE_KEY = 'tomo.collapsedGroups';

function readCollapsed(): ReadonlySet<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const ids: unknown = raw ? JSON.parse(raw) : null;
    return new Set(Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

const collapsedGroups = new Store<ReadonlySet<string>>(readCollapsed());

function writeCollapsed(next: ReadonlySet<string>) {
  collapsedGroups.set(next);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
  } catch {
    /* ignore */
  }
}

export const useCollapsedGroups = (): ReadonlySet<string> => useStore(collapsedGroups);

export function toggleGroup(id: string) {
  const next = new Set(collapsedGroups.get());
  if (!next.delete(id)) next.add(id);
  writeCollapsed(next);
}

/** Open a group so a collection dropped into it doesn't vanish behind a fold. */
export function expandGroup(id: string) {
  if (!collapsedGroups.get().has(id)) return;
  const next = new Set(collapsedGroups.get());
  next.delete(id);
  writeCollapsed(next);
}

/**
 * The sidebar row whose name is being edited. A store rather than component state, so anything
 * that makes a group — the sidebar, the command palette — can hand it straight to its name field.
 */
export const renamingRow = new Store<string | null>(null);
export const startRenaming = (key: string) => renamingRow.set(key);
export const stopRenaming = () => renamingRow.set(null);
export const useRenamingRow = (): string | null => useStore(renamingRow);

/** Make a group and put the cursor in its name: a folder is named, not configured. */
export function newCollectionGroup(project: ProjectDoc) {
  const id = project.createCollectionGroup({ name: 'New group' });
  expandGroup(id);
  startRenaming(groupRowKey(id));
}

/* ---------------------------------------------------------------------------------------------- *
 * Rows
 * ---------------------------------------------------------------------------------------------- */

/**
 * One line of the collection list. Groups and the collections that sit on their own make up the
 * top level (`groupId: null`) and share one order, so `index` is always a position among siblings:
 * among the top-level rows, or among the collections of one group.
 */
export type Row =
  | { key: string; kind: 'group'; groupId: null; group: CollectionGroup; collections: CollectionData[]; index: number; last: boolean }
  | { key: string; kind: 'collection'; groupId: string | null; collection: CollectionData; index: number; last: boolean }
  /** A group with nothing in it yet: something to drop on, and a hint about what to put there. */
  | { key: string; kind: 'empty'; groupId: string; index: number; last: boolean };

export function buildRows(tree: readonly CollectionTreeNode[], collapsed: ReadonlySet<string>): Row[] {
  const rows: Row[] = [];
  const bottom = tree.length - 1;
  tree.forEach((node, i) => {
    if (node.kind === 'collection') {
      rows.push({ key: node.id, kind: 'collection', groupId: null, collection: node.collection, index: i, last: i === bottom });
      return;
    }
    rows.push({ key: groupRowKey(node.id), kind: 'group', groupId: null, group: node.group, collections: node.collections, index: i, last: i === bottom });
    if (collapsed.has(node.id)) return;
    const inner = node.collections.length - 1;
    for (const [j, c] of node.collections.entries()) {
      rows.push({ key: c.id, kind: 'collection', groupId: node.id, collection: c, index: j, last: j === inner });
    }
    if (!node.collections.length) rows.push({ key: `${EMPTY_ROW}${node.id}`, kind: 'empty', groupId: node.id, index: 0, last: true });
  });
  return rows;
}

/* ---------------------------------------------------------------------------------------------- *
 * Drops
 * ---------------------------------------------------------------------------------------------- */

export type Drop = { kind: 'collection'; id: string; groupId: string | null; index: number } | { kind: 'group'; id: string; index: number };

/**
 * What a drop means. Landing on a row takes that row's place in the list it belongs to: land on a
 * collection inside a group and you join the group, land on one that sits on its own and you come
 * back out. A group header is the exception — dropping a collection on a folder puts it inside,
 * which is what dragging onto a folder has always meant — and a group itself only ever moves among
 * the top-level rows, so a row inside a group stands for the group holding it.
 */
export function resolveDrop(tree: readonly CollectionTreeNode[], rows: readonly Row[], activeKey: string, overKey: string): Drop | null {
  if (activeKey === overKey) return null;
  const over = rows.find((r) => r.key === overKey);
  if (!over) return null;
  if (isGroupRow(activeKey)) {
    const id = groupIdFromRow(activeKey);
    // Dragged over one of its own rows, a group has not gone anywhere.
    if (over.groupId === id) return null;
    const index = over.groupId === null ? over.index : tree.findIndex((n) => n.id === over.groupId);
    return index < 0 ? null : { kind: 'group', id, index };
  }
  if (over.kind === 'group') return { kind: 'collection', id: activeKey, groupId: over.group.id, index: 0 };
  if (over.kind === 'empty') return { kind: 'collection', id: activeKey, groupId: over.groupId, index: 0 };
  return { kind: 'collection', id: activeKey, groupId: over.groupId, index: over.index };
}

/** Apply a drop. Groups open as they receive something, so the move stays visible. */
export function applyDrop(project: ProjectDoc, drop: Drop) {
  if (drop.kind === 'group') {
    project.moveCollectionGroup(drop.id, drop.index);
    return;
  }
  if (drop.groupId) expandGroup(drop.groupId);
  project.moveCollection(drop.id, drop.index, drop.groupId);
}
